import { t, tf } from './lib/i18n';
import { useEffect, useState } from 'react';
import {
  blockUser, loadSafetyState, reportUser, unblockUser, type ReportReason, type SafetyContext, type SafetyState
} from './lib/safety';

const reasons: Array<{ value: ReportReason; label: string }> = [
  { value: 'harassment', label: 'Harassment' },
  { value: 'sexual_content', label: 'Sexual content' },
  { value: 'hate_or_abuse', label: 'Hate or abuse' },
  { value: 'spam', label: 'Spam' },
  { value: 'unsafe_behavior', label: 'Unsafe behavior' },
  { value: 'other', label: 'Other' }
];

export function SafetyControls({ initData, context, targets }: {
  initData: string; context: SafetyContext; targets: Array<{ userId: string; firstName: string }>;
}) {
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState<string | null>(targets.length === 1 ? targets[0]?.userId ?? null : null);
  const [state, setState] = useState<SafetyState | null>(null);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contextKey = 'sessionId' in context ? `session:${context.sessionId}` : 'groupRoomId' in context ? `group:${context.groupRoomId}` : `say-it:${context.sayItInteractionId}`;

  useEffect(() => {
    if (!open || !targetId) return;
    let cancelled = false; setBusy(true); setError(null); setState(null);
    void loadSafetyState(initData, targetId, context).then((next) => { if (!cancelled) setState(next); })
      .catch(() => { if (!cancelled) setError(t("Couldn't load safety controls yet.")); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [open, targetId, initData, contextKey]);

  if (!targets.length) return null;
  const selectedTarget = targets.find((target) => target.userId === targetId);
  const perform = async (operation: () => Promise<SafetyState>): Promise<void> => {
    setBusy(true); setError(null);
    try { setState(await operation()); }
    catch { setError(t("Couldn't save that yet.")); }
    finally { setBusy(false); }
  };

  return <>
    <button className="safety-trigger" type="button" onClick={() => setOpen(true)}>{t('SAFETY')}</button>
    {open ? <div className="safety-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
      <section className="safety-sheet" role="dialog" aria-modal="true" aria-labelledby="safety-title">
        <div className="safety-sheet-header"><div><p className="eyebrow">{t('PRIVATE SAFETY')}</p><h2 id="safety-title">{selectedTarget ? selectedTarget.firstName : t('Choose a person')}</h2></div><button className="safety-close" type="button" aria-label={t('Close safety controls')} onClick={() => setOpen(false)}>×</button></div>
        {!targetId ? <div className="safety-targets">{targets.map((target) => <button type="button" key={target.userId} onClick={() => setTargetId(target.userId)}>{target.firstName}</button>)}</div> : <>
          {targets.length > 1 ? <button className="safety-change" type="button" disabled={busy} onClick={() => { setTargetId(null); setState(null); setReason(null); setError(null); }}>{t('CHOOSE SOMEONE ELSE')}</button> : null}
          <p className="safety-copy">{t('Reports are private. Blocking prevents future matching, challenges, group joins, rematches, and SAY IT moments between you.')}</p>
          {state?.reported ? <p className="safety-confirmed">{t('✓ REPORT SAVED')}</p> : <div className="safety-report">
            <p>{t('Why are you reporting this?')}</p><div className="safety-reasons">{reasons.map((item) => <button type="button" key={item.value} className={reason === item.value ? 'selected' : ''} disabled={busy} onClick={() => setReason(item.value)}>{t(item.label)}</button>)}</div>
            <button className="secondary-wide" type="button" disabled={busy || !reason || !selectedTarget} onClick={() => { if (reason && selectedTarget) void perform(() => reportUser(initData, selectedTarget.userId, context, reason)); }}>{busy ? t('SAVING…') : t('SUBMIT REPORT')}</button>
          </div>}
          {state ? <button className={state.blocked ? 'secondary-wide' : 'safety-block'} type="button" disabled={busy || !selectedTarget} onClick={() => {
            if (!selectedTarget) return;
            if (state.blocked) void perform(() => unblockUser(initData, selectedTarget.userId, context));
            else if (window.confirm(tf("Block {name}? You won't be connected again.", { name: selectedTarget.firstName }))) void perform(() => blockUser(initData, selectedTarget.userId, context));
          }}>{state.blocked ? t('UNBLOCK') : tf('BLOCK {name}', { name: selectedTarget?.firstName.toUpperCase() ?? t('PERSON') })}</button> : null}
          {state?.blocked ? <p className="safety-confirmed">{t('✓ BLOCKED')}</p> : null}
          {busy && !state ? <p className="quiet-copy">{t('Loading safety controls…')}</p> : null}
          {error ? <p className="inline-error" role="alert">{error} <button type="button" onClick={() => targetId && void perform(() => loadSafetyState(initData, targetId, context))}>{t('TRY AGAIN')}</button></p> : null}
        </>}
      </section>
    </div> : null}
  </>;
}
